import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { ModalWindow, useLocalizeMessage } from 'libs.nucleus';
import { CloneViewSelect } from './clone_view_select.component';
import { CreateViewForm } from './create_view_form.component';
import { CreateViewOptionSelect } from './create_view_option_select.component';
import { DEFAULT_USE_NEW_INFRASTRUCTURE } from '../../constants/data_view.constants';
import { useDataViews } from '../../hooks';
import { StepFooter } from '../measures_tab/step_footer.component';
import { useStepNavigation } from '../measures_tab/use_step_navigation.hook';
export const CreateViewModal = ({ onClose, onSuccess }) => {
    const translate = useLocalizeMessage();
    const { createDataView, cloneDataView, fetchDataViews, isLoading } = useDataViews();
    // Step 1: Creation option
    const [creationOption, setCreationOption] = useState('create');
    // Clone flow state
    const [selectedViewId, setSelectedViewId] = useState(null);
    const [availableViews, setAvailableViews] = useState([]);
    const [isLoadingViews, setIsLoadingViews] = useState(false);
    // Form state (used for create flow step 2 and clone flow step 3)
    const [formName, setFormName] = useState('');
    const [formDescription, setFormDescription] = useState('');
    const [formUseNewInfrastructure, setFormUseNewInfrastructure] = useState(DEFAULT_USE_NEW_INFRASTRUCTURE);
    const [isFormValid, setIsFormValid] = useState(false);
    // Tracks which source the clone form has been pre-filled for so that picking
    // a different source on step 2 re-seeds the name on step 3.
    const lastPrefillSourceIdRef = useRef(null);
    // Clone flow has 3 steps (option, source, name); create flow has 2 (option, form)
    const totalSteps = creationOption === 'clone' ? 3 : 2;
    // Step validations
    const stepValidations = useMemo(() => ({
        1: () => true, // Step 1 is always valid (selection is always made)
        2: () => {
            if (creationOption === 'clone') {
                return !!selectedViewId;
            }
            return isFormValid;
        },
        3: () => isFormValid,
    }), [creationOption, selectedViewId, isFormValid]);
    const { currentStep, isFirstStep, isLastStep, handleNext, handlePrevious } = useStepNavigation({
        totalSteps,
        stepValidations,
    });
    // Fetch available views when entering clone flow step 2
    useEffect(() => {
        if (creationOption === 'clone' && currentStep === 2) {
            const loadViews = async () => {
                setIsLoadingViews(true);
                try {
                    const views = await fetchDataViews();
                    setAvailableViews(views);
                }
                catch (error) {
                    // Error handled in hook
                }
                finally {
                    setIsLoadingViews(false);
                }
            };
            loadViews();
        }
    }, [creationOption, currentStep, fetchDataViews]);
    // Reset form when creation option changes (but not step - we're still on step 1)
    useEffect(() => {
        setSelectedViewId(null);
        setFormName('');
        setFormDescription('');
        setFormUseNewInfrastructure(DEFAULT_USE_NEW_INFRASTRUCTURE);
        setIsFormValid(false);
        lastPrefillSourceIdRef.current = null;
    }, [creationOption]);
    // Pre-fill the form with the source dataset's name when entering the clone
    // naming step. This mirrors the "Clone asset" flow in ChangeStatusModal:
    // because the source dataset still exists in the library, the uniqueness
    // check inside CreateViewForm will surface "A dataset with this name already
    // exists" until the user picks a different name.
    useEffect(() => {
        if (creationOption !== 'clone' || currentStep !== 3 || !selectedViewId) {
            return;
        }
        if (lastPrefillSourceIdRef.current === selectedViewId) {
            return;
        }
        const sourceView = availableViews.find((v) => v.id === selectedViewId);
        if (!sourceView) {
            return;
        }
        setFormName(sourceView.name);
        setFormDescription('');
        lastPrefillSourceIdRef.current = selectedViewId;
    }, [creationOption, currentStep, selectedViewId, availableViews]);
    const handleFormChange = useCallback((name, description) => {
        setFormName(name);
        setFormDescription(description);
    }, []);
    const handleValidationChange = useCallback((isValid) => {
        setIsFormValid(isValid);
    }, []);
    const handleCloseModal = useCallback(() => {
        setCreationOption('create');
        setSelectedViewId(null);
        setFormName('');
        setFormDescription('');
        setFormUseNewInfrastructure(DEFAULT_USE_NEW_INFRASTRUCTURE);
        setIsFormValid(false);
        lastPrefillSourceIdRef.current = null;
        onClose();
    }, [onClose]);
    const handleSubmit = useCallback(async () => {
        try {
            let newView;
            if (creationOption === 'clone' && selectedViewId) {
                // Clone the selected view using the 'CLONE' option
                // (keeps existing dataset available, creates a copy).
                // Pass name and description overrides so the new dataset uses the
                // unique name the user entered on the naming step.
                const sourceView = availableViews.find((v) => v.id === selectedViewId);
                if (!sourceView) {
                    return;
                }
                newView = await cloneDataView(sourceView, 'CLONE', {
                    name: formName.trim(),
                    description: formDescription.trim(),
                    useNewInfrastructure: formUseNewInfrastructure,
                });
            }
            else {
                // Create a new view
                newView = await createDataView({
                    name: formName.trim(),
                    description: formDescription.trim() || undefined,
                    useNewInfrastructure: formUseNewInfrastructure,
                });
            }
            onSuccess(newView);
            handleCloseModal();
        }
        catch (error) {
            // Error handled in hook
        }
    }, [
        creationOption,
        selectedViewId,
        availableViews,
        cloneDataView,
        createDataView,
        formName,
        formDescription,
        formUseNewInfrastructure,
        onSuccess,
        handleCloseModal,
    ]);
    // Button configurations
    const continueButton = {
        label: translate('Continue'),
        onClick: handleNext,
    };
    const createButton = {
        label: translate('Create'),
        onClick: handleSubmit,
        disabled: isLoading || !isFormValid,
    };
    const footerSecondaryActionButtons = isFirstStep
        ? [
            {
                label: translate('Cancel'),
                onClick: handleCloseModal,
            },
        ]
        : [
            {
                label: translate('Cancel'),
                onClick: handleCloseModal,
            },
            {
                label: translate('Back'),
                onClick: handlePrevious,
            },
        ];
    return (_jsx(ModalWindow, { title: translate('Create dataset'), isOpen: true, closeWindow: handleCloseModal, footerPrimaryActionButton: isLastStep ? createButton : continueButton, footerSecondaryActionButtons: footerSecondaryActionButtons, footerCenterContent: _jsx(StepFooter, { currentStep: currentStep, totalSteps: totalSteps }), width: 'full', children: _jsxs("div", { className: 'min-h-[400px] size-full', children: [currentStep === 1 && (_jsx(CreateViewOptionSelect, { selectedOption: creationOption, onOptionChange: setCreationOption })), currentStep === 2 && creationOption === 'create' && (_jsx(CreateViewForm, { name: formName, description: formDescription, useNewInfrastructure: formUseNewInfrastructure, onFormChange: handleFormChange, onUseNewInfrastructureChange: setFormUseNewInfrastructure, onValidationChange: handleValidationChange })), currentStep === 2 && creationOption === 'clone' && (_jsx(CloneViewSelect, { dataViews: availableViews, isLoading: isLoadingViews, selectedViewId: selectedViewId, onViewSelect: setSelectedViewId })), currentStep === 3 && creationOption === 'clone' && (_jsx(CreateViewForm, { name: formName, description: formDescription, useNewInfrastructure: formUseNewInfrastructure, onFormChange: handleFormChange, onUseNewInfrastructureChange: setFormUseNewInfrastructure, onValidationChange: handleValidationChange }))] }) }));
};
